/**
 * voice-v8 — Werkzeugkarten: Browser-Erweiterung und App-/Ansicht-Öffnen.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { abstandText } from "../relative-time.js";
import { baueKarte, fehlerBlock, hinweisKarte, kartenTitel } from "../karte.js";
import { trimText, trimUrl, unwrapResult } from "./hilfe.js";

// --- Browser-Extension-Tools (Pre.122) -----------------------------------
//
// browser_get_page_text liefert mitunter 5000-zeichen-Page-Dumps. Default-
// Generic-Card hat den 1:1 als JSON in den Chat geschüttet — useless. Hier
// kompakte Cards: Action-Label + 1-2 wichtige Felder. Bot benutzt das
// Result intern, der User sieht nur "was ist passiert + welche Seite".

interface BrowserCardSpec {
  /** Statisch oder dynamisch (basierend auf Tool-Result). */
  label: string | ((r: Record<string, unknown>) => string);
  describe(r: Record<string, unknown>): string;
}

const BROWSER_CARD_SPECS: Record<string, BrowserCardSpec> = {
  browser_open_url: {
    label: "Tab geöffnet",
    describe: (r) => trimUrl((r.url as string | undefined) ?? ""),
  },
  // 28.09.2026 (Ticket 2727): fehlte hier und in der Weiche — die allgemeine
  // Karte schüttete das Ergebnis roh aus. Die Adresse steht als Link darunter.
  browser_tab_context: {
    label: "Aktueller Tab",
    describe: (r) =>
      [
        r.title as string | undefined,
        typeof r.alter_sekunden === "number" ? abstandText(r.alter_sekunden) : "",
      ]
        .filter(Boolean)
        .join(" · "),
  },
  browser_get_active_tab: {
    label: "Aktiver Tab",
    describe: (r) =>
      [
        r.title as string | undefined,
        trimUrl((r.url as string | undefined) ?? ""),
      ]
        .filter(Boolean)
        .join(" · "),
  },
  browser_get_page_text: {
    label: "Seite gelesen",
    describe: (r) => {
      const len = typeof r.length === "number" ? r.length : null;
      const text = typeof r.text === "string" ? r.text : "";
      const preview = text.replace(/\s+/g, " ").trim().slice(0, 100);
      const lenStr = len !== null ? `${len} Zeichen` : "";
      const previewStr = preview.length > 0 ? `„${preview}…"` : "";
      return [lenStr, previewStr].filter(Boolean).join(" · ");
    },
  },
  browser_screenshot: {
    label: "Screenshot",
    // 06.10.2026 (Ticket 3024): seit Ticket 2626 liegt das Foto als Datei (bytes), `data` kommt nicht mehr —
    // die Karte sagte darum bei jedem Foto „leer“.
    describe: (r) => {
      const data = typeof r.data === "string" ? r.data : "";
      const bytes = typeof r.bytes === "number" ? r.bytes : data ? Math.round((data.length * 3) / 4) : 0;
      return bytes ? `${Math.max(1, Math.round(bytes / 1024))} KB PNG` : "leer";
    },
  },
  browser_click: {
    label: "Geklickt",
    describe: (r) => trimText((r.selector as string | undefined) ?? "", 60),
  },
  browser_type: {
    label: "Eingegeben",
    describe: (r) => {
      const sel = trimText((r.selector as string | undefined) ?? "", 30);
      const text = trimText((r.text as string | undefined) ?? "", 40);
      return [sel, text ? `„${text}"` : ""].filter(Boolean).join(" → ");
    },
  },
  // 06.10.2026 (Ticket 3023): wie viele Felder, wie viele Pflichtfelder leer, wie viele mit Fehler.
  browser_form_overview: {
    label: "Formular geprüft",
    describe: (r) => {
      const anzahl = typeof r.anzahl === "number" ? `${r.anzahl} Felder` : "";
      const n = Array.isArray(r.leere_pflichtfelder) ? r.leere_pflichtfelder.length : 0;
      const leer = n ? `${n} ${n === 1 ? "Pflichtfeld" : "Pflichtfelder"} leer` : "";
      const fehler = Array.isArray(r.mit_fehler) && r.mit_fehler.length ? `${r.mit_fehler.length} mit Fehler` : "";
      return [anzahl, leer, fehler].filter(Boolean).join(" · ");
    },
  },
  browser_scroll: {
    label: "Gescrollt",
    describe: (r) => {
      const dir = (r.direction as string | undefined) ?? "";
      const amount = typeof r.amount === "number" ? `${r.amount}px` : "";
      return [dir, amount].filter(Boolean).join(" · ");
    },
  },
  // Pre.232: Step-by-Step-Display mit klarem Status pro Phase.
  // Vorher war es eine lange Zeile mit "·" Trennern — User wollte
  // kompakter + einzelne Schritte sichtbar.
  browser_form_submit: {
    label: (r) => {
      const success = r.success === true;
      const submitOk = (
        r.post_submit as { accepted?: boolean } | null | undefined
      )?.accepted;
      if (success && submitOk === true) return "Formular abgesendet";
      if (success) return "Formular ausgefüllt";
      return "Formular — fehlgeschlagen";
    },
    describe: (r) => {
      const lines: string[] = [];
      const filled = typeof r.filled === "number" ? r.filled : null;
      const total = typeof r.total === "number" ? r.total : null;
      const submitted = typeof r.submitted === "string" ? r.submitted : null;
      const trigger =
        typeof r.trigger_clicked === "string" ? r.trigger_clicked : null;
      const captcha = r.captcha as
        | { question?: string; answer?: string; filled?: boolean }
        | null
        | undefined;
      const post = r.post_submit as
        | {
            accepted?: boolean | null;
            reason?: string;
            message?: string;
          }
        | null
        | undefined;

      const m = (ok: boolean | null, txt: string): string =>
        `${ok === true ? "OK" : ok === false ? "FAIL" : "·"}  ${txt}`;

      if (trigger) lines.push(m(true, `Trigger: „${trimText(trigger, 35)}"`));
      if (filled !== null && total !== null) {
        lines.push(
          m(filled === total, `Felder: ${filled} von ${total} ausgefüllt`),
        );
      }
      if (captcha?.question && captcha?.answer) {
        lines.push(
          m(
            captcha.filled !== false,
            `Captcha: ${captcha.question} = ${captcha.answer}`,
          ),
        );
      }
      // submitted: string (Button-Text) | null (kein Submit gemacht) | false (gesucht aber nicht gefunden — von smart-form als Boolean false zurück)
      const submittedRaw = r.submitted;
      if (submittedRaw === false) {
        lines.push(m(false, "Submit-Button nicht gefunden"));
      } else if (typeof submitted === "string" && submitted) {
        lines.push(m(true, `Submit-Button: „${trimText(submitted, 30)}"`));
      }
      if (post) {
        if (post.accepted === true) {
          lines.push(m(true, `Server: angenommen (${post.reason})`));
        } else if (post.accepted === false) {
          const reason = post.message || post.reason || "abgelehnt";
          lines.push(m(false, `Server: ${trimText(reason, 80)}`));
        } else if (post.accepted === null) {
          lines.push(m(null, `Server: unklar (${post.reason})`));
        }
      }
      return lines.join("\n");
    },
  },
  browser_list_tabs: {
    label: "Tabs gelistet",
    describe: (r) => {
      const tabs = (r.tabs as Array<{ url?: string }> | undefined) ?? [];
      return `${tabs.length} Tabs offen`;
    },
  },
  browser_switch_tab: {
    label: "Tab gewechselt",
    describe: (r) => {
      const id = typeof r.tab_id === "number" ? `#${r.tab_id}` : "";
      return id || "OK";
    },
  },
  browser_close_tab: {
    label: "Tab geschlossen",
    describe: () => "OK",
  },
};

export function buildBrowserCard(tool: string, result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const spec = BROWSER_CARD_SPECS[tool];
  const el = baueKarte(tool.replace(/_/g, "-"));
  const labelStr =
    typeof spec?.label === "function" ? spec.label(r) : (spec?.label ?? tool);
  el.appendChild(kartenTitel(labelStr));
  // Pre.229: Wenn Tool-Output ok=false ODER error gesetzt, prominente
  // Fehlerzeile statt der normalen describe(). Sonst sah der User bei
  // "extension-not-connected" nur "leer" und der Bot ratete dahin.
  const hasError =
    r.ok === false ||
    (typeof r.error === "string" && r.error.trim().length > 0);
  if (hasError) {
    const errMsg =
      typeof r.error === "string" ? r.error : "Tool fehlgeschlagen";
    el.appendChild(fehlerBlock(errMsg));
    return el;
  }
  const detail = spec?.describe(r) ?? "";
  if (detail) {
    const body = document.createElement("div");
    body.className = "tool-browser-detail";
    body.textContent = detail;
    el.appendChild(body);
  }
  // browser_open_url, tab_context + get_active_tab haben URLs — als klickbaren Link.
  const url = typeof r.url === "string" ? r.url : "";
  if (
    url &&
    (tool === "browser_open_url" ||
      tool === "browser_tab_context" ||
      tool === "browser_get_active_tab")
  ) {
    const a = document.createElement("a");
    a.className = "tool-list-link";
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.textContent = trimUrl(url);
    el.appendChild(a);
  }
  return el;
}

export function buildOpenAppCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("open-app");
  const app = typeof r.app === "string" ? r.app : "App";
  const url = typeof r.url === "string" ? r.url : "";
  el.appendChild(kartenTitel("App öffnen"));
  const who = document.createElement("div");
  who.className = "tool-call-name";
  who.textContent = app;
  el.appendChild(who);
  if (url) {
    const a2 = document.createElement("a");
    a2.className = "tool-call-pill tool-call-pill--wide";
    a2.href = url;
    a2.rel = "noopener";
    a2.textContent = `${app} öffnen`;
    el.appendChild(a2);
  }
  return el;
}

export function buildOpenViewCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  // 10.10.2026 (Ticket 3109): geöffnet wurde nichts (z. B. „Nur der Administrator …“) — keine „Geöffnet“-Karte,
  // sondern der Grund. Die Sprachwege setzen isError nur bei einem Absturz, nicht bei ok:false.
  if (r.ok === false) {
    return hinweisKarte(typeof r.error === "string" && r.error ? r.error : "Die Ansicht ließ sich nicht öffnen.");
  }
  const el = baueKarte("open-view");
  const label = typeof r.label === "string" ? r.label : "View";
  el.appendChild(kartenTitel(`Geöffnet: ${label}`));
  return el;
}
